import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimLimitedCostTypeEnum } from 'gql/graphql';

import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
    bimUseActKC2TotalInflationGql,
    resetActKC2CalculatedTotalAllGql,
    resetActKC2CalculatedTotalItemGql,
    setActKC2InputValueGql,
    updateActTotalLimitedGql,
} from '../bimWorkOrderActsKc2Queries';

const UPDATE_REQUEST_ID = 'updateActTotalLimited';
const RESET_ALL_REQUEST_ID = 'resetActKC2AllCalculated';
const RESET_ITEM_REQUEST_ID = 'resetActKC2ItemCalculated';
const SET_INPUT_VALUE_REQUEST_ID = 'setActKC2InputValue';
const SET_TOTAL_INFLATION_SEASON = 'bimUseActKC2TotalInflation';

export const useUpdateActTotalLimited = ({ onSuccess }: { onSuccess: () => void }) => {
    const dispatch = useDispatch();

    /**
     * Применение лимитированных затрат.
     * Кнопки вкл/выкл все над таблицей. Switch в строке
     * */
    const [resetUpdateStoreData] = useResetStoreData({
        requestId: UPDATE_REQUEST_ID,
    });
    const successUpdateHandler = useCallback(() => {
        resetUpdateStoreData(undefined);
        onSuccess();
    }, [onSuccess, resetUpdateStoreData]);

    const [, updateStatus, , makeUpdateRequest] = useRequest({
        requestId: UPDATE_REQUEST_ID,
        query: updateActTotalLimitedGql,
        withEffect: false,
        dependenciesUpdater: successUpdateHandler,
    });

    const updateLimitedHandler = useCallback(
        (requestVariables: { actKC2Id: number; limitedItems: BimLimitedCostTypeEnum[]; useLimited: boolean }) => {
            dispatch(makeUpdateRequest({ ...requestVariables }));
        },
        [dispatch, makeUpdateRequest],
    );

    /**
     * Сбрасывает всё в расчетные значения
     * Кнопка сбросить над таблицей
     * */
    const [resetResetAllStoreData] = useResetStoreData({
        requestId: RESET_ALL_REQUEST_ID,
    });
    const successResetAllHandler = useCallback(() => {
        resetResetAllStoreData(undefined);
        onSuccess();
    }, [onSuccess, resetResetAllStoreData]);

    const [, resetStatus, , makeResetAllRequest] = useRequest({
        requestId: RESET_ALL_REQUEST_ID,
        query: resetActKC2CalculatedTotalAllGql,
        withEffect: false,
        dependenciesUpdater: successResetAllHandler,
    });

    /**
     * Сбрасывает введенное в расчетные значения
     * Кнопка сбросить в строке лимитированной затраты
     * */
    const [resetResetItemStoreData] = useResetStoreData({
        requestId: RESET_ITEM_REQUEST_ID,
    });
    const successResetItemHandler = useCallback(() => {
        resetResetItemStoreData(undefined);
        onSuccess();
    }, [onSuccess, resetResetItemStoreData]);

    const [, resetValueStatus, , makeResetItemRequest] = useRequest({
        requestId: RESET_ITEM_REQUEST_ID,
        query: resetActKC2CalculatedTotalItemGql,
        withEffect: false,
        dependenciesUpdater: successResetItemHandler,
    });

    const resetCalculatedHandler = useCallback(
        (requestVariables: { actKC2Id: number; actKC2TotalItemId?: number }) => {
            if (requestVariables?.actKC2TotalItemId) {
                dispatch(
                    makeResetItemRequest({
                        actKC2Id: requestVariables.actKC2Id,
                        useCalculated: true,
                        actKC2TotalItemId: requestVariables?.actKC2TotalItemId,
                    }),
                );
            } else {
                dispatch(
                    makeResetAllRequest({
                        actKC2Id: requestVariables.actKC2Id,
                        useCalculated: true,
                    }),
                );
            }
        },
        [dispatch, makeResetAllRequest, makeResetItemRequest],
    );

    /**
     * Устанавливает значения в редактируемых строках
     */
    const [resetResetSetInputValueStoreData] = useResetStoreData({
        requestId: SET_INPUT_VALUE_REQUEST_ID,
    });
    const successSetInputValueHandler = useCallback(() => {
        resetResetSetInputValueStoreData(undefined);
        onSuccess();
    }, [onSuccess, resetResetSetInputValueStoreData]);

    const [, setValueStatus, , makeSetInputValueRequest] = useRequest({
        requestId: SET_INPUT_VALUE_REQUEST_ID,
        query: setActKC2InputValueGql,
        withEffect: false,
        dependenciesUpdater: successSetInputValueHandler,
    });

    const setInputValueHandler = useCallback(
        (requestVariables: { actKC2Id: number; actKC2TotalItemId: number; value: number }) => {
            dispatch(makeSetInputValueRequest({ ...requestVariables }));
        },
        [dispatch, makeSetInputValueRequest],
    );

    /**
     * Переключает сезон инфляции
     */
    const [resetBimUseActKC2TotalInflation] = useResetStoreData({
        requestId: SET_TOTAL_INFLATION_SEASON,
    });

    const onUseActKC2TotalInflationSuccess = useCallback(() => {
        resetBimUseActKC2TotalInflation(undefined);
        onSuccess();
    }, [onSuccess, resetBimUseActKC2TotalInflation]);

    const [, bimUseActKC2TotalInflationStatus, , bimUseActKC2TotalInflationRequest] = useRequest({
        requestId: SET_TOTAL_INFLATION_SEASON,
        query: bimUseActKC2TotalInflationGql,
        withEffect: false,
        dependenciesUpdater: onUseActKC2TotalInflationSuccess,
    });

    const setTotalInflationHandler = useCallback(
        (requestVariables: { actKC2Id: number; workOrderSeasonId: number }) => {
            dispatch(bimUseActKC2TotalInflationRequest({ ...requestVariables }));
        },
        [bimUseActKC2TotalInflationRequest, dispatch],
    );

    const isCommitting = [
        setValueStatus,
        updateStatus,
        resetStatus,
        resetValueStatus,
        bimUseActKC2TotalInflationStatus,
    ].some((status) => status === RequestStatus.FETCHING);

    return {
        updateLimitedHandler,
        resetCalculatedHandler,
        setInputValueHandler,
        setTotalInflationHandler,
        isCommitting,
    };
};
